import React, { useState, useCallback } from 'react';
import {
  FileText, Download, Loader2, BarChart3, DollarSign,
  CheckCircle2, AlertTriangle, ChevronDown, ChevronRight, Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useCompany } from '../context/CompanyContext';
import { toast } from 'sonner';
import PageHeader, { headerButtonClass } from '../components/layout/PageHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'balance_sheet', label: 'Balance Sheet', icon: BarChart3 },
  { id: 'profit_loss', label: 'Profit & Loss', icon: DollarSign },
  { id: 'trial_balance', label: 'Trial Balance', icon: CheckCircle2 },
  { id: 'gl_detail', label: 'GL Detail', icon: FileText },
];

const fmt = (n) => {
  if (n === 0) return '-';
  const neg = n < 0;
  const abs = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return neg ? `(${abs})` : abs;
};

// ==================== BALANCE SHEET ====================
const BalanceSheetView = ({ data }) => {
  if (!data) return null;

  const renderSubsection = (sub) => (
    <div key={sub.name} className="mb-3">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 pl-4">{sub.name}</p>
      {sub.items?.map(([name, amount], i) => (
        <div key={i} className="flex justify-between px-6 py-1 text-sm hover:bg-slate-50">
          <span className="text-slate-700">{name}</span>
          <span className={`font-mono ${amount < 0 ? 'text-red-600' : 'text-slate-800'}`}>{fmt(amount)}</span>
        </div>
      ))}
      <div className="flex justify-between px-6 py-1 text-sm font-semibold border-t border-slate-200 mt-1">
        <span className="text-slate-600">Total {sub.name}</span>
        <span className="font-mono">{fmt(sub.total)}</span>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <h2 className="text-lg font-bold text-slate-900">Balance Sheet</h2>
        <p className="text-sm text-slate-500">As of {data.as_of}</p>
      </div>

      {data.sections?.map(section => (
        <Card key={section.name}>
          <CardHeader className="py-3 px-4 bg-slate-50">
            <CardTitle className="text-base">{section.name}</CardTitle>
          </CardHeader>
          <CardContent className="p-0 divide-y">
            {section.subsections?.map(sub => renderSubsection(sub))}
            {section.items && !section.subsections && (
              <div className="py-2">
                {section.items.map(([name, amount], i) => (
                  <div key={i} className="flex justify-between px-6 py-1 text-sm hover:bg-slate-50">
                    <span className="text-slate-700">{name}</span>
                    <span className={`font-mono ${amount < 0 ? 'text-red-600' : 'text-slate-800'}`}>{fmt(amount)}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-between px-4 py-2 font-bold bg-slate-100">
              <span>Total {section.name}</span>
              <span className="font-mono">{fmt(section.total)}</span>
            </div>
          </CardContent>
        </Card>
      ))}

      <Card className={`border-2 ${data.balanced ? 'border-emerald-300' : 'border-red-300'}`}>
        <CardContent className="p-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              {data.balanced
                ? <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                : <AlertTriangle className="w-5 h-5 text-red-500" />}
              <span className="font-semibold">{data.balanced ? 'Balance Sheet is balanced' : 'Balance Sheet is NOT balanced'}</span>
            </div>
            <div className="text-right text-sm">
              <p>Total Assets: <span className="font-mono font-bold">{fmt(data.total_assets)}</span></p>
              <p>Total L&E: <span className="font-mono font-bold">{fmt(data.total_liabilities_equity)}</span></p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== PROFIT & LOSS ====================
const ProfitLossView = ({ data }) => {
  if (!data) return null;

  return (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <h2 className="text-lg font-bold text-slate-900">Profit & Loss Statement</h2>
        <p className="text-sm text-slate-500">{data.start_date} through {data.end_date}</p>
      </div>

      {data.sections?.map(section => (
        <Card key={section.name}>
          <CardHeader className="py-3 px-4 bg-slate-50">
            <CardTitle className="text-base">{section.name}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {section.items?.map(([name, amount], i) => (
              <div key={i} className="flex justify-between px-6 py-1.5 text-sm hover:bg-slate-50 border-b border-slate-100 last:border-0">
                <span className="text-slate-700">{name}</span>
                <span className={`font-mono ${amount < 0 ? 'text-red-600' : 'text-slate-800'}`}>{fmt(amount)}</span>
              </div>
            ))}
            <div className="flex justify-between px-4 py-2 font-bold bg-slate-100">
              <span>Total {section.name}</span>
              <span className="font-mono">{fmt(section.total)}</span>
            </div>
          </CardContent>
        </Card>
      ))}

      <div className="grid grid-cols-3 gap-4">
        <Card className="p-4 text-center">
          <p className="text-xs text-slate-500">Gross Profit</p>
          <p className={`text-xl font-bold ${data.gross_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>${fmt(data.gross_profit)}</p>
        </Card>
        <Card className="p-4 text-center">
          <p className="text-xs text-slate-500">Total Expenses</p>
          <p className="text-xl font-bold text-red-600">${fmt(data.total_expenses)}</p>
        </Card>
        <Card className={`p-4 text-center border-2 ${data.net_income >= 0 ? 'border-emerald-300' : 'border-red-300'}`}>
          <p className="text-xs text-slate-500">Net Income</p>
          <p className={`text-xl font-bold ${data.net_income >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>${fmt(data.net_income)}</p>
        </Card>
      </div>
    </div>
  );
};

// ==================== TRIAL BALANCE ====================
const TrialBalanceView = ({ data }) => {
  if (!data) return null;

  return (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <h2 className="text-lg font-bold text-slate-900">Trial Balance</h2>
        <p className="text-sm text-slate-500">As of {data.as_of}</p>
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm" data-testid="trial-balance-table">
            <thead>
              <tr className="border-b bg-slate-50 text-left">
                <th className="p-3 font-medium text-slate-600 w-20">Acct #</th>
                <th className="p-3 font-medium text-slate-600">Account Name</th>
                <th className="p-3 font-medium text-slate-600">Type</th>
                <th className="p-3 font-medium text-slate-600 text-right">Debit</th>
                <th className="p-3 font-medium text-slate-600 text-right">Credit</th>
              </tr>
            </thead>
            <tbody>
              {data.accounts?.map(acct => (
                <tr key={acct.number} className="border-b hover:bg-slate-50">
                  <td className="p-3 font-mono text-slate-600">{acct.number}</td>
                  <td className="p-3 font-medium">{acct.name}</td>
                  <td className="p-3 text-xs text-slate-500">{acct.type}</td>
                  <td className="p-3 text-right font-mono">{acct.debit > 0 ? fmt(acct.debit) : ''}</td>
                  <td className="p-3 text-right font-mono">{acct.credit > 0 ? fmt(acct.credit) : ''}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-100 font-bold">
                <td className="p-3" colSpan={3}>Totals</td>
                <td className="p-3 text-right font-mono">{fmt(data.total_debits)}</td>
                <td className="p-3 text-right font-mono">{fmt(data.total_credits)}</td>
              </tr>
            </tfoot>
          </table>
        </CardContent>
      </Card>

      <Card className={`border-2 ${data.balanced ? 'border-emerald-300' : 'border-red-300'}`}>
        <CardContent className="p-3 flex items-center gap-2">
          {data.balanced
            ? <CheckCircle2 className="w-5 h-5 text-emerald-500" />
            : <AlertTriangle className="w-5 h-5 text-red-500" />}
          <span className="font-medium">{data.balanced ? 'Trial Balance is in balance' : 'Trial Balance is OUT of balance'}</span>
          <span className="text-sm text-slate-500 ml-auto">
            Difference: {fmt(Math.abs(data.total_debits - data.total_credits))}
          </span>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== GL DETAIL ====================
const GLDetailView = ({ data }) => {
  const [expandedAccts, setExpandedAccts] = useState({});
  if (!data) return null;

  const toggle = (num) => setExpandedAccts(prev => ({ ...prev, [num]: !prev[num] }));

  return (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <h2 className="text-lg font-bold text-slate-900">General Ledger Detail</h2>
        <p className="text-sm text-slate-500">{data.start_date} through {data.end_date}</p>
        <p className="text-xs text-slate-400">{data.account_count} accounts with activity</p>
      </div>

      {data.sections?.map(section => (
        <Card key={section.account_number}>
          <button
            className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
            onClick={() => toggle(section.account_number)}
            data-testid={`gl-acct-${section.account_number}`}
          >
            <div className="flex items-center gap-3">
              {expandedAccts[section.account_number] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              <span className="font-mono text-sm text-slate-500">{section.account_number}</span>
              <span className="font-medium">{section.account_name}</span>
              <span className="text-xs text-slate-400 bg-slate-200 px-2 py-0.5 rounded">{section.account_type}</span>
            </div>
            <div className="flex gap-6 text-sm">
              <span>Dr: <span className="font-mono font-bold">{fmt(section.total_debit)}</span></span>
              <span>Cr: <span className="font-mono font-bold">{fmt(section.total_credit)}</span></span>
              <span className={`font-bold font-mono ${section.net_balance >= 0 ? 'text-slate-800' : 'text-red-600'}`}>
                Net: {fmt(section.net_balance)}
              </span>
            </div>
          </button>
          {expandedAccts[section.account_number] && (
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-slate-500">
                    <th className="p-2 pl-4">Date</th>
                    <th className="p-2">Type</th>
                    <th className="p-2">Ref</th>
                    <th className="p-2">Description</th>
                    <th className="p-2 text-right">Debit</th>
                    <th className="p-2 text-right">Credit</th>
                    <th className="p-2 text-right">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {section.transactions?.map((t, i) => (
                    <tr key={i} className="border-b hover:bg-slate-50">
                      <td className="p-2 pl-4 text-slate-600">{t.date}</td>
                      <td className="p-2">
                        <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100">{t.type}</span>
                      </td>
                      <td className="p-2 font-mono text-xs text-slate-500">{t.ref}</td>
                      <td className="p-2 text-slate-700">{t.description}</td>
                      <td className="p-2 text-right font-mono">{t.debit > 0 ? fmt(t.debit) : ''}</td>
                      <td className="p-2 text-right font-mono">{t.credit > 0 ? fmt(t.credit) : ''}</td>
                      <td className={`p-2 text-right font-mono font-medium ${t.running_balance < 0 ? 'text-red-600' : ''}`}>
                        {fmt(t.running_balance)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          )}
        </Card>
      ))}

      {(!data.sections || data.sections.length === 0) && (
        <Card className="p-12 text-center">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No GL transactions found for this period</p>
          <p className="text-xs text-slate-400 mt-1">Post transactions from the Posting Queue to see data here</p>
        </Card>
      )}
    </div>
  );
};

// ==================== MAIN PAGE ====================
const FinancialReportsPage = () => {
  const { companyId } = useCompany();
  const [activeTab, setActiveTab] = useState('balance_sheet');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear(), 0, 1);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [accountFilter, setAccountFilter] = useState('');
  const [includePending, setIncludePending] = useState(true);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setReportData(null);
    try {
      const params = new URLSearchParams();
      if (endDate) params.append('end_date', endDate);
      if (['profit_loss', 'gl_detail'].includes(activeTab) && startDate) params.append('start_date', startDate);
      if (activeTab === 'gl_detail' && accountFilter) params.append('account', accountFilter);
      if (includePending) params.append('include_pending', 'true');
      // without this, every company's reports showed everyone's combined numbers (see docs/development-plan-2026-09.md)
      params.append('company_id', companyId || 'default');

      const urlMap = {
        balance_sheet: 'balance-sheet',
        profit_loss: 'profit-loss',
        trial_balance: 'trial-balance',
        gl_detail: 'gl-detail',
      };

      const res = await fetch(`${API_URL}/api/reports/financial/${urlMap[activeTab]}?${params}`);
      if (res.ok) {
        setReportData(await res.json());
      } else {
        toast.error('Failed to load report');
      }
    } catch { toast.error('Error loading report'); }
    setLoading(false);
  }, [activeTab, startDate, endDate, accountFilter, includePending, companyId]);

  const downloadPDF = async () => {
    const typeMap = { balance_sheet: 'balance_sheet', profit_loss: 'profit_loss', trial_balance: 'trial_balance', gl_detail: 'gl_detail' };
    try {
      const res = await fetch(`${API_URL}/api/reports/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ report_type: typeMap[activeTab], start_date: startDate, end_date: endDate, format: 'pdf', company_id: companyId || 'default' }),
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${activeTab}_${endDate}.pdf`;
        a.click();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not save. Please try again.');
      }
    } catch { toast.error('Download failed'); }
  };

  return (
    <div className="space-y-6" data-testid="financial-reports-page">
      <PageHeader
        title="Financial Reports"
        subtitle="Balance Sheet, Profit & Loss, Trial Balance and General Ledger Detail"
        tabs={TABS.map(({ id, label }) => ({ id, label }))}
        activeTab={activeTab}
        onTab={(id) => { setActiveTab(id); setReportData(null); }}
        tabTestIdPrefix="tab-"
        actions={
          <button type="button" className={headerButtonClass} onClick={downloadPDF} data-testid="download-pdf-btn">
            <Download className="w-4 h-4" /> Download PDF
          </button>
        }
      />

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-end gap-4 flex-wrap">
            {['profit_loss', 'gl_detail'].includes(activeTab) && (
              <div>
                <label className="text-xs font-medium text-slate-500">Start Date</label>
                <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-44" data-testid="start-date-input" />
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-slate-500">
                {['balance_sheet', 'trial_balance'].includes(activeTab) ? 'As of Date' : 'End Date'}
              </label>
              <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-44" data-testid="end-date-input" />
            </div>
            {activeTab === 'gl_detail' && (
              <div>
                <label className="text-xs font-medium text-slate-500">Account # (optional)</label>
                <Input value={accountFilter} onChange={e => setAccountFilter(e.target.value)} placeholder="e.g. 1000" className="w-32" data-testid="account-filter-input" />
              </div>
            )}
            <Button onClick={loadReport} disabled={loading} data-testid="generate-report-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Filter className="w-4 h-4 mr-2" />}
              Generate Report
            </Button>
            <label className="flex items-center gap-2 text-sm text-slate-600 ml-4 mt-1">
              <input type="checkbox" checked={includePending} onChange={e => setIncludePending(e.target.checked)} data-testid="include-pending-toggle" />
              Include pending transactions
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Report Content */}
      {loading && (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
        </div>
      )}

      {!loading && reportData && activeTab === 'balance_sheet' && <BalanceSheetView data={reportData} />}
      {!loading && reportData && activeTab === 'profit_loss' && <ProfitLossView data={reportData} />}
      {!loading && reportData && activeTab === 'trial_balance' && <TrialBalanceView data={reportData} />}
      {!loading && reportData && activeTab === 'gl_detail' && <GLDetailView data={reportData} />}

      {!loading && !reportData && (
        <Card className="p-12 text-center">
          <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Select a report and click Generate Report</p>
          <p className="text-xs text-slate-400 mt-1">Reports are generated from posted GL transactions</p>
        </Card>
      )}
    </div>
  );
};

export default FinancialReportsPage;
